import {getCopilotSpacePath} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {CopilotSpaceActorRole, SpaceCollaborator} from '@github-ui/copilot-chat-types'
import {copilotSpacesQueryKey} from '@github-ui/copilot-shared-ui/spaces/fetch'
import {useMutation, useQuery, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

export type CollaboratorPermission = {
  actor_id: number
  role_name?: CopilotSpaceActorRole
  actor_type: 'user' | 'team'
}

export const useSpaceCollaborators = (spaceOwner: string, spaceNumber: number) => {
  const queryClient = useQueryClient()
  const spacePath = getCopilotSpacePath({
    id: spaceNumber,
    owner: spaceOwner,
  })
  const basePath = `${spacePath}/permissions`

  const {data: collaborators, isLoading} = useQuery({
    queryKey: ['copilotSpaceCollaborators', basePath],
    queryFn: async () => {
      const response = await verifiedFetchJSON(basePath)
      const data = await response.json()

      if (!response.ok) {
        throw data.errorMessages
      }

      return data as SpaceCollaborator[]
    },
  })

  const {mutateAsync: addCollaborator, ...addRest} = useMutation({
    mutationFn: async (input: CollaboratorPermission): Promise<SpaceCollaborator> => {
      const response = await verifiedFetchJSON(basePath, {
        method: 'POST',
        body: {permission: input},
      })

      const data = await response.json()

      if (!response.ok) {
        throw data.errorMessages
      }

      return data as SpaceCollaborator
    },
    onSuccess: () => {
      const copilotSpaceId = {id: spaceNumber, owner: spaceOwner}
      void queryClient.invalidateQueries({queryKey: copilotSpacesQueryKey(copilotSpaceId)})
      return queryClient.invalidateQueries({queryKey: ['copilotSpaceCollaborators', basePath]})
    },
  })

  const {mutateAsync: updateCollaborator, ...updateRest} = useMutation({
    mutationFn: async (input: CollaboratorPermission): Promise<SpaceCollaborator> => {
      const response = await verifiedFetchJSON(basePath, {
        method: 'PUT',
        body: {permission: input},
      })

      const data = await response.json()

      if (!response.ok) {
        throw data.errorMessages
      }

      return data as SpaceCollaborator
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({queryKey: ['copilotSpaceCollaborators', basePath]})
    },
  })

  const {mutateAsync: removeCollaborator, ...removeRest} = useMutation({
    mutationFn: async (input: CollaboratorPermission): Promise<void> => {
      const response = await verifiedFetchJSON(basePath, {
        method: 'DELETE',
        body: {permission: input},
      })

      if (!response.ok) {
        const data = await response.json()
        throw data.errorMessages
      }
    },
    onSuccess: () => {
      const copilotSpaceId = {id: spaceNumber, owner: spaceOwner}
      void queryClient.invalidateQueries({queryKey: copilotSpacesQueryKey(copilotSpaceId)})
      return queryClient.invalidateQueries({queryKey: ['copilotSpaceCollaborators', basePath]})
    },
  })

  return {
    collaborators,
    isLoading,
    addCollaborator,
    updateCollaborator,
    removeCollaborator,
    isAddPending: addRest.isPending,
    isUpdatePending: updateRest.isPending,
    isRemovePending: removeRest.isPending,
    ...addRest,
    ...updateRest,
    ...removeRest,
  }
}
